import type { FixtureElement } from "./types";

export interface PropertyValueCount {
  readonly value: unknown;
  readonly count: number;
}

export interface SelectionInspection {
  readonly selectedCount: number;
  readonly typeCounts: Readonly<Record<string, number>>;
  readonly propertyCount: number;
  readonly visiblePropertyCount: number;
  readonly sharedProperties: Readonly<Record<string, unknown>>;
  readonly mixedProperties: Readonly<Record<string, readonly PropertyValueCount[]>>;
  readonly unknownRecordCount: number;
  readonly unsupportedCount: number;
  readonly unmappedCount: number;
}

export interface CodeCatalogItem {
  readonly libraryId: string;
  readonly version: string;
  readonly system: "Uniclass 2015" | "OmniClass";
  readonly code: string;
  readonly label: string;
  readonly status: "candidate";
}

export interface ComponentCatalogItem {
  readonly libraryId: string;
  readonly version: string;
  readonly componentId: string;
  readonly label: string;
  readonly appliesTo: readonly string[];
  readonly status: "candidate";
}

export const codeCatalog: readonly CodeCatalogItem[] = [
  {
    libraryId: "classification-candidate",
    version: "demo-1",
    system: "Uniclass 2015",
    code: "Ss_25_10_30",
    label: "Wall systems (candidate)",
    status: "candidate"
  },
  {
    libraryId: "classification-candidate",
    version: "demo-1",
    system: "OmniClass",
    code: "23-13 13",
    label: "Wall components (candidate)",
    status: "candidate"
  }
];

export const componentCatalog: readonly ComponentCatalogItem[] = [
  {
    libraryId: "component-candidate",
    version: "demo-1",
    componentId: "wall-standard-a",
    label: "Standard wall specification A",
    appliesTo: ["Wall"],
    status: "candidate"
  },
  {
    libraryId: "component-candidate",
    version: "demo-1",
    componentId: "door-standard-a",
    label: "Standard door specification A",
    appliesTo: ["Door"],
    status: "candidate"
  }
];

function valueKey(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "string") return `string:${value}`;
  if (typeof value === "number") return `number:${value}`;
  if (typeof value === "boolean") return `boolean:${value}`;
  if (typeof value === "undefined") return "undefined";
  return JSON.stringify(value) ?? "undefined";
}

export function inspectSelection(elements: readonly FixtureElement[], selectedIds: ReadonlySet<string>): SelectionInspection {
  return inspectElements(elements.filter((element) => selectedIds.has(element.id)), Number.MAX_SAFE_INTEGER);
}

export function inspectElements(
  elements: readonly FixtureElement[],
  propertyLimit = Number.MAX_SAFE_INTEGER
): SelectionInspection {
  if (!Number.isSafeInteger(propertyLimit) || propertyLimit < 0) {
    throw new RangeError("Inspect property limit must be a non-negative safe integer.");
  }
  const selected = [...elements].sort((left, right) => left.id.localeCompare(right.id));
  const typeCounts: Record<string, number> = {};
  const propertyNamesSet = new Set<string>();
  let unknownRecordCount = 0;
  let unsupportedCount = 0;
  let unmappedCount = 0;
  for (const element of selected) {
    typeCounts[element.type] = (typeCounts[element.type] ?? 0) + 1;
    for (const property of Object.keys(element.properties)) propertyNamesSet.add(property);
    if (Object.keys(element.unknown).length > 0) unknownRecordCount += 1;
    if (element.unsupportedReason !== undefined) unsupportedCount += 1;
    if (element.classification === null) unmappedCount += 1;
  }

  const propertyNames = [...propertyNamesSet].sort();
  const visiblePropertyNames = propertyNames.slice(0, propertyLimit);
  const sharedProperties: Record<string, unknown> = {};
  const mixedProperties: Record<string, readonly PropertyValueCount[]> = {};
  const missingValue = { state: "missing" } as const;

  for (const property of visiblePropertyNames) {
    const counts = new Map<string, { value: unknown; count: number }>();
    for (const element of selected) {
      const value = Object.prototype.hasOwnProperty.call(element.properties, property)
        ? element.properties[property]
        : missingValue;
      const key = valueKey(value);
      const existing = counts.get(key);
      counts.set(key, { value, count: (existing?.count ?? 0) + 1 });
    }
    const values = [...counts.values()];
    if (values.length === 1 && values[0].count === selected.length) sharedProperties[property] = values[0].value;
    else mixedProperties[property] = values;
  }

  return {
    selectedCount: selected.length,
    typeCounts,
    propertyCount: propertyNames.length,
    visiblePropertyCount: visiblePropertyNames.length,
    sharedProperties,
    mixedProperties,
    unknownRecordCount,
    unsupportedCount,
    unmappedCount
  };
}

export interface EditablePropertyOptions {
  readonly editableCount: number;
  readonly propertyNames: readonly string[];
}

/**
 * Real property names available for the bulk property editor, restricted to elements in the
 * selection that are actually writer-supported (`editable && unsupportedReason === undefined`).
 *
 * A selection is allowed to mix editable and read-only/unsupported elements -- that stays useful
 * for inspecting a read-only element's real property names before editing an editable one -- but
 * the property *picker* must never itself offer a name that cannot possibly be staged as a real
 * replace. Before this, the picker only suggested real names (still let the user type anything),
 * so typing a synthetic display field that is not a key in any editable element's `properties`
 * (e.g. an IFC entity class name, which is never itself a property) silently staged as a "missing"
 * before-state, got reclassified downstream as an "add" operation, and refused the whole BIM-Hub
 * commit as writer-unsupported -- with the failure only surfacing after Send to BIM-Hub, not while
 * choosing what to edit (kaz, 2026-08-12).
 */
export function editablePropertyOptions(elements: readonly FixtureElement[]): EditablePropertyOptions {
  const editable = elements.filter((element) => element.editable && element.unsupportedReason === undefined);
  const propertyNames = [...new Set(editable.flatMap((element) => Object.keys(element.properties)))].sort();
  return { editableCount: editable.length, propertyNames };
}

export type CurrentPropertyValueState =
  | { readonly state: "shared"; readonly text: string }
  | { readonly state: "missing" }
  | { readonly state: "mixed" }
  | { readonly state: "non-primitive" };

function primitiveText(value: unknown): string | null {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return null;
}

/**
 * What to prefill the bulk editor's Value field with once a property is chosen, restricted to the
 * editable subset -- the only elements a "Replace all" can actually touch. Before this, the Value
 * field just kept whatever was last typed for a *different* property, so switching the property
 * picker left a stale, unrelated value sitting in the box (kaz, 2026-08-12).
 *
 * Only ever offers a single unambiguous primitive to prefill: `missing` if any editable element in
 * the selection lacks the property, `mixed` if the editable elements disagree, `non-primitive` if
 * the shared value isn't a string/number/boolean (an object/array can't round-trip a single text
 * input). All three leave the field for the user to fill in explicitly rather than guess.
 */
export function currentEditablePropertyValue(elements: readonly FixtureElement[], property: string): CurrentPropertyValueState {
  const editable = elements.filter((element) => element.editable && element.unsupportedReason === undefined);
  let text: string | null = null;
  let seenAny = false;
  for (const element of editable) {
    if (!Object.prototype.hasOwnProperty.call(element.properties, property)) return { state: "missing" };
    const candidate = primitiveText(element.properties[property]);
    if (candidate === null) return { state: "non-primitive" };
    if (!seenAny) {
      text = candidate;
      seenAny = true;
    } else if (text !== candidate) {
      return { state: "mixed" };
    }
  }
  return seenAny ? { state: "shared", text: text as string } : { state: "missing" };
}

export function codeProposalValue(item: CodeCatalogItem): string {
  return `${item.libraryId}@${item.version}:${item.system}:${item.code}`;
}

export function componentProposalValue(item: ComponentCatalogItem): string {
  return `${item.libraryId}@${item.version}:${item.componentId}`;
}

export function componentApplicability(
  item: ComponentCatalogItem,
  elements: readonly FixtureElement[],
  selectedIds: ReadonlySet<string>
): { readonly applicable: boolean; readonly unsupportedElementIds: readonly string[] } {
  const unsupportedElementIds = elements
    .filter((element) => selectedIds.has(element.id) && !item.appliesTo.includes(element.type))
    .map((element) => element.id)
    .sort();
  return { applicable: selectedIds.size > 0 && unsupportedElementIds.length === 0, unsupportedElementIds };
}
